import { FormError, Input, Label, Select, Textarea } from '@ctek/design-system';
import { ErrorMessage, Field, FieldProps } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { SelectOptions } from './options';
import { ReactNode } from 'react';

type FormFieldProp = { name: string };

export const FormikTextInput = ({ id, name, ...props }: React.InputHTMLAttributes<HTMLInputElement> & FormFieldProp) => (
  <Field id={name} name={name}>
    {({ field, form }: FieldProps) => (
      <Input type="text" {...field} {...props} autoComplete="off" qa={`text-input-${name}`} onChange={onChangeResetErrorHandler(field, form, name)} />
    )}
  </Field>
);

export const FormikTextArea = ({ id, name, ...props }: React.ComponentProps<typeof Textarea> & FormFieldProp) => (
  <Field id={name} name={name} disabled={true}>
    {({ field, form }: FieldProps) => (
      <Textarea {...field} {...props} name={name} qa={`textarea-${name}`} onChange={onChangeResetErrorHandler(field, form, name)} />
    )}
  </Field>
);

export const FormikDatePicker = ({ id, name, ...props }: React.InputHTMLAttributes<HTMLInputElement> & FormFieldProp) => (
  <Field id={name} name={name}>
    {({ field, form }: FieldProps) => (
      <Input
        {...field}
        {...props}
        type="date"
        autoComplete="off"
        qa={`date-picker-${name}`}
        onChange={onChangeResetErrorHandler(field, form, name)}
      />
    )}
  </Field>
);

export const FormikTimePicker = ({ id, name, ...props }: React.InputHTMLAttributes<HTMLInputElement> & FormFieldProp) => (
  <Field id={name} name={name}>
    {({ field, form }: FieldProps) => (
      <Input
        {...field}
        {...props}
        type="time"
        autoComplete="off"
        qa={`time-picker-${name}`}
        onChange={onChangeResetErrorHandler(field, form, name)}
      />
    )}
  </Field>
);

export const FormikSelect = ({
  id,
  name,
  options,
  ...props
}: React.InputHTMLAttributes<HTMLSelectElement> & FormFieldProp & { options: SelectOptions }) => {
  const { formatMessage } = useIntl();
  return (
    <Field id={name} name={name}>
      {({ field, form }: FieldProps) => (
        <Select {...field} {...props} qa={`select-${name}`} onChange={onChangeResetErrorHandler(field, form, name)}>
          {options.map(option => (
            <Select.Option key={`${name}-${option.value}`} value={option.value}>
              {formatMessage({ id: option.messageId, defaultMessage: option.defaultMessage })}
            </Select.Option>
          ))}
        </Select>
      )}
    </Field>
  );
};

export const FormikError = ({
  name,
  errorValues,
}: React.InputHTMLAttributes<HTMLInputElement> & FormFieldProp & { errorValues?: Record<string, any> }) => (
  <ErrorMessage name={name}>
    {(message: string) => (
      <FormError qa={`${name}-error`}>{<FormattedMessage id={message} defaultMessage={message} values={errorValues} />}</FormError>
    )}
  </ErrorMessage>
);

export const LabelWithError = ({ name, label, children }: FormFieldProp & { label: ReactNode; children: ReactNode }) => {
  return (
    <>
      <Label htmlFor={name}>
        <Label.Text>{label}</Label.Text>
        {children}
      </Label>
      <FormikError name={name} />
    </>
  );
};

const onChangeResetErrorHandler =
  <T,>(field: FieldProps['field'], form: FieldProps['form'], name: string) =>
  (event: React.ChangeEvent<T>) => {
    form.setFieldError(name, undefined);
    field.onChange(event);
  };
